import {RESPONSE_ID_PREFIX, MARKDOWN_HEADING_OFFSET} from './constants.js';
import {createStreamingRenderer} from '../incrementalMarkdown.js';
import {initMarked, renderMarkdown} from '../../vendor/marked/service.js';
import {escapeHtml} from '../../vendor/marked/utils.js';
import {chatMessageEl, markdownEl, toolsEl, toolItemEl} from './elements.js';

const renderMakeResponse = (jobId, chatLogContainer) => (res, data) => {
    const {progress} = res;
    const id = `${RESPONSE_ID_PREFIX}${jobId}`;

    if (!chatLogContainer.querySelector(`#${id}`)) {
        const el = chatMessageEl(false);
        el.id = id;

        // Create and append AI response
        const contentEl = markdownEl();
        el.appendChild(contentEl);

        // Create and append tool status
        const toolStatusEl = toolsEl();
        el.appendChild(toolStatusEl);

        // Initialize streaming renderer
        const streamingRenderer = createStreamingRenderer(contentEl, el, {
            headingOffset: MARKDOWN_HEADING_OFFSET,
            renderDelay: 16
        });

        // Store renderer reference for cleanup
        el._streamingRenderer = streamingRenderer;
        el._toolStatusEl = toolStatusEl;
        el._toolItems = {};

        // Append to chat log
        chatLogContainer.appendChild(el);
    }

    const el = chatLogContainer.querySelector(`#${id}`);
    const streamingRenderer = el._streamingRenderer;
    const toolStatusEl = el._toolStatusEl;
    const toolItems = el._toolItems || {};

    const getFileName = (path) => {
        if (!path) {
            return '';
        }

        const parts = String(path).split('/');

        return parts[parts.length - 1] || String(path);
    };

    const setToolItemVariant = (itemObj, variant) => {
        const el = itemObj.el;
        el.classList.remove('is-success', 'is-error', 'is-info');

        switch (variant) {
            case 'success':
                el.classList.add('is-success');
                break;
            case 'error':
                el.classList.add('is-error');
                break;
            case 'info':
                el.classList.add('is-info');
                break;
        }
    };

    const ensureToolItem = (key) => {
        if (toolItems[key]) {
            return toolItems[key];
        }

        const toolItem = toolItemEl();

        if (toolStatusEl) {
            toolStatusEl.appendChild(toolItem.root);
            toolItem.root.scrollIntoView();
        }

        toolItems[key] = {
            el: toolItem.root,
            spinner: toolItem.spinner,
            text: toolItem.text
        };
        el._toolItems = toolItems;

        return toolItems[key];
    };

    const ensureJobItem = () => {
        if (el._jobItem) {
            return el._jobItem;
        }

        const jobItem = toolItemEl();

        if (toolStatusEl) {
            toolStatusEl.appendChild(jobItem.root);
            jobItem.root.scrollIntoView();
        }

        el._jobItem = {
            el: jobItem.root,
            spinner: jobItem.spinner,
            text: jobItem.text
        };

        return el._jobItem;
    };

    const setToolItemText = (tool, status, path) => {
        const file = getFileName(path);

        switch (tool) {
            case 'createOrUpdateFile':
                if (status === 'success') return `Edited ${file}`;
                if (status === 'error') return `Failed editing ${file}`;
                return file ? `Editing ${file}…` : 'Editing…';
            case 'readFile':
                return status === 'success'
                    ? `Read file ${file}`
                    : 'Reading file…';
            case 'writeFile':
                return status === 'success'
                    ? `Wrote ${file}`
                    : `Writing ${file}`;
            case 'appendToFile':
                return status === 'success'
                    ? `Appended to ${file}`
                    : 'Appending to file…';
            case 'stringReplace':
                return status === 'success'
                    ? `Replaced text in ${file}`
                    : 'Replacing text…';
            case 'snapshotProjectFiles':
                return status === 'success'
                    ? 'Scanned project files'
                    : 'Scanning project files…';
            default:
                return status === 'success' ? 'Done' : 'Working…';
        }
    };

    if (progress && progress.length > 0) {
        progress.forEach((item) => {
            const progress = item.progress || {};

            // Update tool item based on tool progress events
            if (progress.event === 'tool_progress') {
                const {tool_name, status, message, path, bytes, project_hash} =
                    progress;

                console.log(
                    `[tool] ${tool_name || 'unknown'}: ${status || 'info'} - ${message || ''}`,
                    {path, bytes, project_hash}
                );

                const key = `${tool_name || 'tool'}:${path || ''}`;
                const item = ensureToolItem(key);

                item.text.textContent = setToolItemText(
                    tool_name,
                    status,
                    path
                );
                item.spinner.style.display =
                    status === 'success' || status === 'error' ? 'none' : '';

                switch (status) {
                    case 'start':
                        setToolItemVariant(item, 'info');
                        break;
                    case 'success':
                        setToolItemVariant(item, 'success');
                        break;
                    case 'error':
                        setToolItemVariant(item, 'error');
                        break;
                }
            }

            if (progress.event === 'job_status') {
                const {status, message} = progress;
                console.log(`[job] ${status} - ${message || ''}`);

                const item = ensureJobItem();

                if (status === 'building') {
                    item.text.textContent = `Build in progress…`;
                }

                if (status === 'build_completed_successfully') {
                    item.text.textContent = `Build completed successfully`;
                    item.spinner.style.display = 'none';
                }

                if (status === 'build_failed') {
                    setToolItemVariant(item, 'error');
                    item.text.textContent = `Build failed. ${message}`;
                    item.spinner.style.display = 'none';
                }
            }

            if (progress.event === 'planner_status') {
                const {status, message} = progress;
                const item = ensureJobItem();

                if (status === 'thinking') {
                    item.text.textContent = message || 'Thinking…';
                }
            }

            // Suppress verbose backend text per updated UI

            if (
                streamingRenderer &&
                !streamingRenderer.isFinalized() &&
                progress.delta
            ) {
                streamingRenderer.appendDelta(progress.delta);
            }
        });

        data.set('p_last_progress', progress[progress.length - 1].id);
    }
    return {ok: true, data};
};

const renderUserPrompt = async (prompt, chatLogContainer) => {
    const el = chatMessageEl();
    const contentEl = markdownEl();
    el.appendChild(contentEl);

    const markedInstance = await initMarked(MARKDOWN_HEADING_OFFSET);
    const escapedPrompt = escapeHtml(prompt);
    contentEl.innerHTML = await renderMarkdown(escapedPrompt, markedInstance);

    chatLogContainer.appendChild(el);
    el.scrollIntoView();
};

export {renderMakeResponse, renderUserPrompt};
